import { S3Object } from '../../../drive/types';
import { ensureTrailingSlash, getPathSeparator, isDirectory } from '../helpers';
import { useModalState } from './useModalState';

export interface ShareItem {
  shareUrl: string;
  s3ReferenceUrl: string;
  itemName: string;
  isDirectory: boolean;
  itemKey: string;
}

export const useShareOperations = () => {
  const shareAccessModal = useModalState<ShareItem>();
  const copyLinkModal = useModalState<ShareItem>();

  const splitHref = (href: string): { baseHref: string; query: string | null } => {
    const [baseHref, query = null] = href.split('?');
    return { baseHref, query };
  };

  const ensureDirUrl = (href: string): string => ensureTrailingSlash(href);

  const withSingleSelect = (href: string, filename: string): string => {
    try {
      const url = new URL(href);
      url.searchParams.delete('select');
      url.searchParams.append('select', filename);
      return url.toString();
    } catch {
      const base = href.split('?')[0];
      const sep = href.includes('?') ? '&' : '?';
      return `${base}${sep}select=${encodeURIComponent(filename)}`;
    }
  };

  const generateShareUrl = (currentUrl: string, itemName?: string, isItemDirectory?: boolean): string => {
    // No item name → current directory URL
    if (!itemName) return currentUrl;

    // For files, keep users on the directory route (avoid ingress 404),
    // the filename will be in ?select=
    if (!isItemDirectory) return currentUrl;

    // For directories, append and ensure trailing slash
    const separator = getPathSeparator(currentUrl);
    const baseUrl = `${currentUrl}${separator}${itemName}`;
    return ensureTrailingSlash(baseUrl);
  };

  const generateS3ReferenceUrl = (currentUrl: string, itemKey?: string): string => {
    const driveIndex = currentUrl.indexOf('/drive/');
    if (driveIndex === -1) return '';

    const afterDrive = currentUrl.substring(driveIndex + '/drive/'.length);
    const firstSlashIndex = afterDrive.indexOf('/');

    // Extract bucket (workspace ID)
    const bucket = firstSlashIndex === -1 ? afterDrive : afterDrive.substring(0, firstSlashIndex);

    // If itemKey provided, use it; otherwise extract path from URL
    const path = itemKey || (firstSlashIndex === -1 ? '' : afterDrive.substring(firstSlashIndex + 1));

    return path ? `s3://${bucket}/${path}` : `s3://${bucket}/`;
  };

  const getItemDisplayName = (item: S3Object): string => {
    if (item.isParentDirectory) return 'Parent Directory';

    const key = item.Key || '';

    if (isDirectory(key)) {
      const pathWithoutTrailingSlash = key.slice(0, -1);
      const lastSlashIndex = pathWithoutTrailingSlash.lastIndexOf('/');
      return lastSlashIndex === -1 ? pathWithoutTrailingSlash : pathWithoutTrailingSlash.substring(lastSlashIndex + 1);
    }

    const lastSlashIndex = key.lastIndexOf('/');
    return lastSlashIndex === -1 ? key : key.substring(lastSlashIndex + 1);
  };

  const buildShareItem = (item: S3Object, currentPath?: string): ShareItem | null => {
    if (!item.Key) return null;

    const isItemDirectory = isDirectory(item.Key);
    const itemName = getItemDisplayName(item);

    // For nested items, we need the relative path from current directory
    let relativePath = itemName;
    if (currentPath && item.Key.startsWith(currentPath)) {
      // Extract the relative path from the current directory
      const normalizedCurrentPath = ensureTrailingSlash(currentPath);
      relativePath = item.Key.substring(normalizedCurrentPath.length);
    }

    const href = window.location.href;
    const { baseHref } = splitHref(href);
    // Always work from a normalized directory URL
    let shareUrl = generateShareUrl(baseHref, relativePath, isItemDirectory);

    // For files, add a single select param with just the filename
    if (!isItemDirectory) shareUrl = withSingleSelect(shareUrl, relativePath);

    const s3ReferenceUrl = generateS3ReferenceUrl(href, item.Key);
    return {
      shareUrl,
      s3ReferenceUrl,
      itemName,
      isDirectory: isItemDirectory,
      itemKey: item.Key,
    };
  };

  const handleCopyLinkItem = (item: S3Object, currentPath?: string) => {
    const data = buildShareItem(item, currentPath);
    if (data) copyLinkModal.open(data);
  };

  const handleShareAccessItem = (item: S3Object, currentPath?: string) => {
    const data = buildShareItem(item, currentPath);
    if (data) shareAccessModal.open(data);
  };

  const handleCopyLinkDirectory = () => {
    const currentUrl = window.location.href;

    const driveIndex = currentUrl.indexOf('/drive/');
    if (driveIndex === -1) {
      copyLinkModal.open({
        shareUrl: ensureDirUrl(currentUrl),
        s3ReferenceUrl: '',
        itemName: 'Current Directory',
        isDirectory: true,
        itemKey: '',
      });
      return;
    }

    const afterDrive = currentUrl.substring(driveIndex + '/drive/'.length);
    const firstSlashIndex = afterDrive.indexOf('/');

    let shareUrl;
    if (firstSlashIndex === -1) {
      shareUrl = ensureDirUrl(currentUrl);
    } else {
      const bucketPart = currentUrl.substring(0, driveIndex + '/drive/'.length + firstSlashIndex);
      const pathAfterBucket = afterDrive.substring(firstSlashIndex);

      if (isDirectory(currentUrl)) {
        shareUrl = ensureDirUrl(bucketPart + pathAfterBucket);
      } else {
        shareUrl = ensureDirUrl(bucketPart);
      }
    }

    const s3ReferenceUrl = generateS3ReferenceUrl(currentUrl);

    // Extract itemKey from current URL for presigned URL generation
    const afterDriveWithPath = afterDrive.substring(firstSlashIndex === -1 ? afterDrive.length : firstSlashIndex + 1);
    const itemKey = afterDriveWithPath || '';

    copyLinkModal.open({
      shareUrl,
      s3ReferenceUrl,
      itemName: 'Current Directory',
      isDirectory: true,
      itemKey,
    });
  };

  const handleShareAccessDirectory = () => {
    // Preserve query parameters (e.g., ?select=...) when sharing the current directory
    const fullHref = window.location.href;
    const { baseHref, query } = splitHref(fullHref);

    const driveIndex = baseHref.indexOf('/drive/');
    const afterDrive = driveIndex === -1 ? '' : baseHref.substring(driveIndex + '/drive/'.length);
    const firstSlashIndex = afterDrive.indexOf('/');

    // Determine itemKey for current directory
    const afterDriveWithPath = afterDrive.substring(firstSlashIndex === -1 ? afterDrive.length : firstSlashIndex + 1);
    const itemKey = afterDriveWithPath || '';

    // Share URL for current directory should be the current URL normalized
    let shareUrl = ensureDirUrl(baseHref);
    if (query) shareUrl = `${shareUrl}?${query}`;

    shareAccessModal.open({
      shareUrl,
      s3ReferenceUrl: generateS3ReferenceUrl(fullHref),
      itemName: 'Current Directory',
      isDirectory: true,
      itemKey,
    });
  };

  return {
    // Share Access modal state
    shareAccessModal,
    // Copy Link modal state
    copyLinkModal,
    handleCopyLinkItem,
    handleCopyLinkDirectory,
    handleShareAccessItem,
    handleShareAccessDirectory,
  };
};
